import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Link, UserCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Button } from '../../Shared/Button/Button';
import { Separator } from '../../Shared/Separator/Separator';
import { ApiActionBox } from '../ApiAction/ApiActionBox';
import { QueryKey } from '@/Config/api/queryKey';
import { IndexPath } from '@/Config/app/router/index.path';
import {
  HttpActionDto,
  HttpActionFromTextDto,
  HttpRequestAIActionFromTextMode,
} from '@/Domain/api/Action/dto/Action.dto';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/Domain/components/Shared/Dialog/Dialog';
import { MODAL_NAMES, useModalStore } from '@/Domain/store/modal.store';
import { findSecretPlaceholders } from '@/Domain/utils/placeholders';
import { getCurrentScenarioId } from '@/context';

type HttpRequestModalParams = {
  mode: HttpRequestAIActionFromTextMode;
  httpRequestAIActionFromText: HttpActionFromTextDto | null;
};

const SECTION_LABEL = 'text-xs font-semibold uppercase tracking-wide';

export const HttpRequestAIActionFromTextDialog = () => {
  const { t } = useTranslation('http_request_ai_action', { keyPrefix: 'from_text' });
  const { isOpen, closeModal, toggleModal, params } = useModalStore();
  const navigate = useNavigate();

  const { mode, httpRequestAIActionFromText } =
    ((params ?? null) as HttpRequestModalParams | null) || {};

  const needsInput =
    mode != HttpRequestAIActionFromTextMode.CREATED &&
    httpRequestAIActionFromText?.outcome === 'needs_input';

  const title =
    mode == HttpRequestAIActionFromTextMode.CREATED
      ? t('title_success')
      : needsInput
        ? t('title_needs_input')
        : t('title_failed');
  const description =
    mode == HttpRequestAIActionFromTextMode.CREATED ? t('description_success') : '';

  const { data: action } = useQuery<HttpActionDto>({
    queryKey: [
      QueryKey.actions.httpRequest.get,
      httpRequestAIActionFromText?.http_request_ai_action?.id,
    ],
  });

  const placeholdersToReplace = action
    ? findSecretPlaceholders(
        action.url,
        action.headers,
        action.body_items,
        action.query_items,
        action.body_json,
      )
    : [];

  if (!httpRequestAIActionFromText) {
    return null;
  }

  const openHumanHandoff = () => {
    closeModal();
    navigate(`/i/${getCurrentScenarioId()}/${IndexPath.actions}`);
    toggleModal(MODAL_NAMES.HANDOFF_ACTION_SHEET);
  };

  const handoffCta = httpRequestAIActionFromText.suggested_builtin_action === 'human_handoff' && (
    <Button
      variant="dark"
      onClick={openHumanHandoff}
      data-testid="from-text-open-handoff"
      className="gap-2"
    >
      <UserCheck className="h-4 w-4" />
      {t('open_human_handoff')}
    </Button>
  );

  const placeholdersSection = placeholdersToReplace.length > 0 && (
    <section className="space-y-2" data-testid="from-text-placeholders">
      <p className={`${SECTION_LABEL} flex items-center gap-1.5 text-warning`}>
        <AlertTriangle className="h-3.5 w-3.5" />
        {t('placeholders_title')}
      </p>
      <div className="rounded-md border border-warning/30 bg-warning-muted p-3 text-sm text-muted-foreground">
        <p className="mb-2">{t('placeholders_hint')}</p>
        <ul className="space-y-1 font-mono text-foreground">
          {placeholdersToReplace.map((placeholder) => (
            <li key={placeholder}>{placeholder}</li>
          ))}
        </ul>
      </div>
    </section>
  );

  const detailsSection = (
    <section className="space-y-2">
      <p className={`${SECTION_LABEL} text-foreground-subtle`}>{t('creation_details')}</p>
      <div className="rounded-md border border-border bg-muted p-3 text-sm text-muted-foreground whitespace-pre-wrap">
        {httpRequestAIActionFromText.explainer_message}
      </div>
    </section>
  );

  const sourcesSection = (
    <section className="space-y-2">
      <p className={`${SECTION_LABEL} text-foreground-subtle`}>{t('sources')}</p>
      {!httpRequestAIActionFromText.citations ||
      httpRequestAIActionFromText.citations.length === 0 ? (
        <p className="text-sm text-foreground-subtle italic">{t('no_sources')}</p>
      ) : (
        <ul className="space-y-2 text-sm text-muted-foreground">
          {httpRequestAIActionFromText.citations.map((citation, index) => (
            <li
              key={`${citation.url}.${index}`}
              className="flex gap-2 rounded-md border border-border bg-card p-3"
            >
              <div className="mt-0.5">
                <Link className="h-4 w-4 text-foreground-subtle" />
              </div>
              <div className="flex flex-col">
                <a
                  href={citation.url}
                  target="_blank"
                  rel="noreferrer"
                  className="font-medium text-primary hover:underline break-all"
                >
                  {citation.url}
                </a>
                <p className="text-xs text-foreground-subtle mt-1">{citation.description}</p>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );

  return (
    <Dialog
      open={isOpen(MODAL_NAMES.HTTP_REQUEST_AI_ACTION_FROM_TEXT_MODAL)}
      onOpenChange={closeModal}
    >
      <DialogContent
        className="w-[70vw] max-w-[750px] max-h-[85vh] flex flex-col gap-3"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="w-lg">{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <Separator />

        <div className="overflow-auto custom-scroll" data-testid="http-ai-action-from-text-dialog">
          <div className="mt-6 space-y-6">
            {action ? (
              <div className="grid items-start gap-5 md:grid-cols-[16rem_minmax(0,1fr)]">
                <div className="w-64 items-start text-left">
                  <ApiActionBox key={action.id} action={action} isPreview />
                </div>
                <div className="min-w-0 space-y-6">
                  {placeholdersSection}
                  {detailsSection}
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {detailsSection}
                {handoffCta}
              </div>
            )}
            {sourcesSection}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
